/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from 'src/@vymo/ui/atoms/button';
import classnames from 'classnames';
import { ReactComponent as Cross } from 'src/assets/icons/close.svg';
import { getAppRootElement } from 'src/workspace/utils';
import Body from './body';
import Footer from './footer';
import Header, { HeaderProps } from './header';
import { ModalProps } from './types';
import styles from './index.module.scss';

function Modal({
  open = true,
  onClose = _.noop,
  closeOnEscape = true,
  showCloseButton = true,
  children,
  'data-test-id': dataTestId,
  classNames,
  style,
  onBackdropClick,
  variant = 'default',
  noPadding = false,
}: React.PropsWithChildren<ModalProps>) {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const [showHeaderCloseButton, setShowHeaderCloseButton] = useState(false);

  const closeOnEscapeKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (closeOnEscape) {
      document.body.addEventListener('keydown', closeOnEscapeKeyDown);
    }
    return () => {
      document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
    };
  }, [closeOnEscape, closeOnEscapeKeyDown]);

  useEffect(() => {
    const childrenArray = React.Children.toArray(children);
    const containsHeader = childrenArray.some(
      (child: any) => child?.type === Header,
    );
    setShowHeaderCloseButton(containsHeader && showCloseButton);
  }, [children, showCloseButton]);

  const modalClasses = classnames(styles.modal, classNames, {
    [styles.modal__padding]: !noPadding,
  });

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      modalRef?.current &&
      !modalRef?.current?.contains(event.target as Node)
    ) {
      onBackdropClick?.();
    }
  };

  const modalContent = (
    // TODO - Swapnil fix this
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      data-test-id={`${dataTestId}-wrapper`}
      className={styles.modal__backdrop}
      onClick={handleBackdropClick}
    >
      <div className={modalClasses} style={style} ref={modalRef}>
        {showCloseButton && !showHeaderCloseButton && (
          <Button
            data-test-id={`${dataTestId}-close-button`}
            size="small"
            type="text"
            iconProps={{ icon: <Cross />, iconPosition: 'right' }}
            className={styles.modal__closeButton}
            onClick={onClose}
          />
        )}
        {React.Children.map(children, (child) => {
          // @ts-ignore
          const isValid = [Header, Body, Footer].includes(child?.type);

          if (!isValid) {
            return 'Invalid Modal Children. Modal children can be of Header , Body or Footer';
          }

          if (
            // @ts-ignore
            child?.type === Header &&
            React.isValidElement<HeaderProps>(child)
          ) {
            return React.cloneElement(child, {
              showHeaderCloseButton,
              onClose,
              variant,
            });
          }

          return child;
        })}
      </div>
    </div>
  );

  return open ? createPortal(modalContent, getAppRootElement()) : null;
}

export default Modal;

export { Header, Body, Footer };
